import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import "./admin.css";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [serverStatus, setServerStatus] = useState("checking"); // "checking", "ok", "down"
  const [renderStatusUrl] = useState("https://render.com/status");

  const checkServerHealth = useCallback(async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/health`, {
        method: "GET",
        headers: { "Content-Type": "application/json" }
      });

      const data = await res.json();

      // Log detailed diagnostics for developers
      console.group("🏥 Server Health Check");
      console.log("Status:", data.status);
      console.log("Timestamp:", data.timestamp);
      console.log("Checks:", data.checks);
      if (data.checks.cosmosDiagnostics) {
        console.group("Azure Cosmos DB Diagnostics");
        console.log("Endpoint:", data.checks.cosmosDiagnostics.endpoint);
        console.log("Connection Status:", data.checks.cosmosDiagnostics.connectionStatus);
        console.log("Database Status:", data.checks.cosmosDiagnostics.databaseStatus);
        console.log("Diagnostic Info:", data.checks.cosmosDiagnostics.diagnosticInfo);
        if (Object.keys(data.checks.cosmosDiagnostics.containers || {}).length > 0) {
          console.log("Containers:", data.checks.cosmosDiagnostics.containers);
        }
        console.groupEnd();
      }
      console.groupEnd();

      if (res.ok && data.status === "OK") {
        setServerStatus("ok");
      } else if (res.status === 207 || data.status === "DEGRADED") {
        setServerStatus("down");
        setError("Some services are currently unavailable. Please check back shortly.");
      } else {
        setServerStatus("down");
        setError("Server connection failed. The service may be temporarily unavailable.");
      }
    } catch (err) {
      console.error("Health check failed:", err);
      setServerStatus("down");
      setError(
        `Unable to connect to server. Please check your internet connection or ` +
        `check Render status at: ${renderStatusUrl}`
      );
    }
  }, [renderStatusUrl]);

  // Check server health on component mount
  useEffect(() => {
    checkServerHealth();
  }, [checkServerHealth]);

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      setError("Email and password are required");
      return;
    }

    // Check if server is down
    if (serverStatus === "down") {
      setError(
        `Server is currently unavailable. Please check Render status at: ${renderStatusUrl}`
      );
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();

      if (res.ok) {
        if (data.role === "fms") {
          setError("This login is for Super Admin and Tower Admins. Use the FMS Login page instead.");
          setLoading(false);
          return;
        }

        localStorage.setItem("token", data.token);
        localStorage.setItem("adminRole", data.role);
        if (data.tower) localStorage.setItem("adminTower", data.tower);

        window.location.href = "/admin/dashboard";
      } else if (res.status === 503) {
        setServerStatus("down");
        setError(
          `Database connection failed. The Render infrastructure may be experiencing issues. ` +
          `Check status at: ${renderStatusUrl}`
        );
      } else {
        setError(data.message || "Invalid admin credentials");
      }
    } catch (err) {
      console.error("Login error:", err);
      setServerStatus("down");
      setError(
        `Network error. Please check your connection or ` +
        `check Render status at: ${renderStatusUrl}`
      );
    }

    setLoading(false);
  };

  return (
    <div className="admin-login-container">
      <h1>Super Admin / Tower Admin Login</h1>

      {/* Server Status Indicator */}
      <div
        className={`server-status-indicator ${serverStatus}`}
        style={{
          marginBottom: "20px",
          padding: "12px",
          borderRadius: "6px",
          fontSize: "13px",
          textAlign: "center",
          backgroundColor:
            serverStatus === "ok"
              ? "#d4edda"
              : serverStatus === "down"
              ? "#f8d7da"
              : "#e7e7e7",
          color:
            serverStatus === "ok"
              ? "#155724"
              : serverStatus === "down"
              ? "#721c24"
              : "#666",
          fontWeight: "500"
        }}
      >
        {serverStatus === "checking" && "🔄 Checking server status..."}
        {serverStatus === "ok" && "✅ Server is online"}
        {serverStatus === "down" && (
          <>
            ⚠️ Server offline - Check{" "}
            <a
              href={renderStatusUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "inherit", textDecoration: "underline" }}
            >
              Render status
            </a>
            {" "}or{" "}
            <button
              onClick={checkServerHealth}
              style={{
                background: "none",
                border: "none",
                color: "inherit",
                textDecoration: "underline",
                cursor: "pointer",
                padding: 0
              }}
            >
              retry
            </button>
          </>
        )}
      </div>

      <form onSubmit={handleLogin} className="admin-login-form">
        <input
          type="email"
          placeholder="Admin Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={serverStatus === "down"}
        />

        <input
          type="password"
          placeholder="Admin Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={serverStatus === "down"}
        />

        {error && <p className="admin-error">{error}</p>}

        <button type="submit" disabled={loading || serverStatus === "down"}>
          {loading
            ? "Logging in..."
            : serverStatus === "down"
            ? "Server Offline"
            : "Login"}
        </button>
      </form>

      <p className="tower-admin-link">
        Managing a tower? <Link to="/admin/register-tower">Register as Tower Admin</Link>
      </p>

      <p className="tower-admin-link">
        <Link to="/">← Back to Crown Residences</Link>
      </p>
    </div>
  );
}